/**
 * PATH: packages/ui/src/kanban/utils/boardBackground.ts
 * FILE_ID: UI-KANBAN-BOARD-BG-001
 * PURPOSE: Pure input->output helpers for editing a board's background
 *          appearance, stored in `Board.config_json`.
 * WHY:
 *   FEAT-2026-06-15-KANBAN-BOARD-BG-RIGHTCLICK — operator: "Allow me to
 *   change the color of the background by right clicking and settings".
 *   The board theme engine (theme.ts) already reads four appearance keys
 *   from config_json (theme_preset, bg_color, bg_image_id, bg_image_url),
 *   but the only UI that wrote them (the header "..." Background picker)
 *   exposed presets + scenic images + a custom URL — never a free-form
 *   colour, and there was no right-click entry point. These helpers are
 *   the pure decision core shared by the new right-click canvas menu AND
 *   the (de-forked) header picker, so the config-mutation rules live in
 *   ONE tested place rather than being duplicated inline.
 * FLOW:
 *   current config_json -> apply<X>(cfg, value) -> next config_json
 * DEPENDENCIES: none (stdlib only — trivially unit-testable, no React).
 * RELATED:
 *   - ../theme.ts: getBoardTheme reads these same keys.
 *   - ./KanbanBoardBackgroundPicker.tsx: the picker UI that calls these.
 */

export type BoardConfig = Record<string, unknown>

/** Light canvas colours offered as quick picks in the custom-colour row.
 *  LIGHT-FIRST (2026-06-15) — operator: "Always use default light colors
 *  on kanban background. Dark colors aren't good for visibility." These
 *  are soft pastel tints (distinct from the 8 BOARD_THEMES presets) so
 *  the custom row adds reach rather than duplicating them, and every
 *  quick pick keeps the board light + legible. The native
 *  <input type="color"> still covers anything not in this set; whatever
 *  the operator picks is rendered as a light hue-wash by theme.ts. */
export const BOARD_BG_COLOR_SWATCHES: string[] = [
  '#fde2e4', // blush rose
  '#ffe5d9', // peach
  '#fff1cc', // sand
  '#d8f3dc', // mint
  '#d0f4f0', // aqua
  '#d7ecfb', // sky
  '#e2e0fb', // periwinkle
  '#ede0fb', // lilac
  '#fce1f0', // pink
  '#eceff3', // stone
  '#e5efe0', // sage
  '#fdf6e3', // cream
]

const HEX_RE = /^#?[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/

/** Normalise a user-typed hex (with/without `#`, 3- or 6-digit) to a
 *  canonical lowercase `#rrggbb`. Returns null when the input isn't a
 *  valid hex colour so callers can ignore garbage rather than persist
 *  it. Native <input type="color"> already emits `#rrggbb`, so this
 *  mainly guards the free-text hex field. */
export function normalizeHexColor(raw: string): string | null {
  const v = (raw ?? '').trim()
  if (!HEX_RE.test(v)) return null
  let h = v.replace('#', '')
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return '#' + h.toLowerCase()
}

/** Apply a named theme preset. Clears any free-form `bg_color` so the
 *  preset wins (matches the legacy header-picker behaviour); leaves an
 *  existing image overlay untouched (an image stacks on the preset base
 *  per theme.ts's resolution order). Pure — returns a new object. */
export function applyThemePreset(cfg: BoardConfig, preset: string): BoardConfig {
  return { ...cfg, theme_preset: preset, bg_color: '' }
}

/** Apply a free-form background colour. Clears the preset (so the colour
 *  is honoured over any named preset) AND the image keys — a scenic
 *  image would otherwise cover the canvas and hide the colour the
 *  operator just chose, defeating "change the colour". Pure. */
export function applyBgColor(cfg: BoardConfig, hex: string): BoardConfig {
  return {
    ...cfg,
    bg_color: hex,
    theme_preset: '',
    bg_image_id: '',
    bg_image_url: '',
  }
}

/** Apply a curated background image by id. Mirrors the legacy header
 *  picker: clears any operator-pasted URL so the curated pick wins;
 *  leaves colour/preset as the base palette the image overlays. Pure. */
export function applyBgImageId(cfg: BoardConfig, id: string): BoardConfig {
  return { ...cfg, bg_image_id: id, bg_image_url: '' }
}

/** Apply an operator-pasted background image URL. Clears the curated id
 *  so the URL wins (URL takes precedence in theme.ts). Pure. */
export function applyBgImageUrl(cfg: BoardConfig, url: string): BoardConfig {
  return { ...cfg, bg_image_url: url, bg_image_id: '' }
}

/** Reset all background customisation -> the board falls back to its
 *  default/auto theme (JOB/TRELLO_MIRROR hash colour, or neutral). Pure. */
export function clearBackground(cfg: BoardConfig): BoardConfig {
  return {
    ...cfg,
    theme_preset: '',
    bg_color: '',
    bg_image_id: '',
    bg_image_url: '',
  }
}

/** Read the currently-saved free-form colour (normalised) or '' when
 *  none is set / it isn't a valid string. */
export function currentBgColor(cfg: BoardConfig | null | undefined): string {
  const v = (cfg as { bg_color?: unknown } | null | undefined)?.bg_color
  if (typeof v !== 'string' || !v) return ''
  return normalizeHexColor(v) ?? ''
}

/** True when the board currently has ANY explicit background
 *  customisation (preset, colour, or image). Drives whether the
 *  "Reset to default" affordance is shown. */
export function hasCustomBackground(cfg: BoardConfig | null | undefined): boolean {
  const c = (cfg ?? {}) as {
    theme_preset?: unknown
    bg_color?: unknown
    bg_image_id?: unknown
    bg_image_url?: unknown
  }
  return Boolean(c.theme_preset || c.bg_color || c.bg_image_id || c.bg_image_url)
}

/** Appearance keys written by the background picker / canvas menu. */
const BOARD_BACKGROUND_KEYS = [
  'theme_preset',
  'bg_color',
  'bg_image_id',
  'bg_image_url',
] as const

export type BoardBackgroundPatch = {
  theme_preset: unknown
  bg_color: unknown
  bg_image_id: unknown
  bg_image_url: unknown
}

/** Max bg_image_url length on the wire. Larger data-URLs trip the gateway
 *  (browser reports CORS / Failed to fetch). Scenic picks use bg_image_id. */
export const BOARD_BG_IMAGE_URL_MAX_CHARS = 48_000

/**
 * Minimal PATCH body for a background change.
 *
 * Same failure class as ``ganttBucketUnitPatch``: re-sending the WHOLE
 * merged ``config_json`` (data-URL backgrounds, ``card_hours``, etc.)
 * makes the PATCH megabyte-scale. The gateway drops the oversized body
 * before the app responds, so the browser reports CORS / Failed to fetch.
 * Backend deep-merges ``config_json``, so only the four appearance keys
 * need to travel on the wire. Pure I→O — callers still use the full
 * ``next`` object for optimistic local theme reads.
 *
 * Throws when ``bg_image_url`` is a megabyte-scale data-URL so the UI can
 * show a real error instead of a silent CORS failure.
 */
export function boardBackgroundPatch(next: BoardConfig): BoardBackgroundPatch {
  const out: BoardBackgroundPatch = {
    theme_preset: '',
    bg_color: '',
    bg_image_id: '',
    bg_image_url: '',
  }
  for (const key of BOARD_BACKGROUND_KEYS) {
    if (key in next) out[key] = next[key]
  }
  const url = out.bg_image_url
  if (typeof url === 'string' && url.length > BOARD_BG_IMAGE_URL_MAX_CHARS) {
    throw new Error(
      'Background image URL is too large to save. Use a hosted image link or a scenic preset instead of pasting a data URL.',
    )
  }
  return out
}
